import React from "react";
import { NavLink, Outlet, useNavigate, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useAuth } from "@/context/AuthContext";
import { LayoutDashboard, FileText, Boxes, Inbox, Users, LogOut, ExternalLink, MessageSquareQuote, Images, FilePlus2, Navigation, Lock, Scissors } from "lucide-react";
import GlobalPreviewPane from "@/components/admin/GlobalPreviewPane";

const LOGO_URL = "/atc-logo.png";

const NAV_ITEMS = [
  { to: "/admin", label: "Overview", icon: LayoutDashboard, end: true, testId: "admin-nav-overview" },
  { to: "/admin/pages", label: "Home CMS", icon: FileText, testId: "admin-nav-pages" },
  { to: "/admin/navigation", label: "Navigation", icon: Navigation, testId: "admin-nav-navigation" },
  { to: "/admin/site-pages", label: "Site Pages", icon: FilePlus2, testId: "admin-nav-site-pages" },
  { to: "/admin/packages", label: "Packages", icon: Boxes, testId: "admin-nav-packages" },
  { to: "/admin/testimonials", label: "Testimonials", icon: MessageSquareQuote, testId: "admin-nav-testimonials" },
  { to: "/admin/gallery", label: "Before & After", icon: Images, testId: "admin-nav-gallery" },
  { to: "/admin/leads", label: "Leads", icon: Inbox, testId: "admin-nav-leads" },
  { to: "/admin/contact-form", label: "Contact Form", icon: FilePlus2, testId: "admin-nav-contact-form" },
  { to: "/admin/portal", label: "Dealer Portal", icon: Lock, testId: "admin-nav-portal" },
  { to: "/admin/image-editor", label: "Image Editor", icon: Scissors, testId: "admin-nav-image-editor" },
  { to: "/admin/admins", label: "Admins", icon: Users, testId: "admin-nav-admins" },
];

const AdminLayout = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate("/admin/login");
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 flex">
      <Helmet>
        <meta name="robots" content="noindex, nofollow, noarchive" />
        <title>Admin · ATC Mold & Water</title>
      </Helmet>
      <aside className="w-64 shrink-0 bg-slate-950 border-r border-white/5 flex flex-col">
        <div className="px-6 py-6 flex items-center gap-3 border-b border-white/5">
          <img src={LOGO_URL} alt="ATC" className="h-10 w-10 object-contain" />
          <div className="leading-tight">
            <div className="font-display font-black text-sm text-white">ATC Admin</div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500">Restoration CMS</div>
          </div>
        </div>
        <nav className="p-4 space-y-1 flex-1">
          {NAV_ITEMS.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition ${
                  isActive
                    ? "bg-slate-900 text-white border border-white/10"
                    : "text-slate-400 hover:text-white hover:bg-slate-900/50"
                }`
              }
              data-testid={item.testId}
            >
              <item.icon className="h-4 w-4" />
              {item.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-4 border-t border-white/5 space-y-2">
          <Link to="/" target="_blank" className="flex items-center gap-2 px-3 py-2 text-xs text-slate-400 hover:text-white" data-testid="admin-view-site">
            <ExternalLink className="h-3.5 w-3.5" /> View public site
          </Link>
          {user && (
            <div className="px-3 py-2 text-xs text-slate-400 truncate" data-testid="admin-user-email">
              {user.email}
              {user.is_super_admin && <span className="ml-2 text-atc-green">[super]</span>}
            </div>
          )}
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-xs text-slate-300 hover:text-white hover:bg-slate-900"
            data-testid="admin-logout"
          >
            <LogOut className="h-3.5 w-3.5" /> Sign out
          </button>
        </div>
      </aside>

      <main className="flex-1 bg-slate-900/40 overflow-y-auto min-w-0">
        <Outlet />
      </main>
      <GlobalPreviewPane />
    </div>
  );
};

export default AdminLayout;
